import axios from 'axios';
import {
  appVersion,
  baseUrl,
  clientType,
} from '../../../shared/api/apiClientConfig';

const publicApi = axios.create({
  baseURL: baseUrl,
  timeout: 15000,
  headers: {
    'X-Client-Version': appVersion,
    'X-Client-Type': clientType,
  },
});

export interface AuthenticationResponse {
  identityToken: string;
  refreshToken: string;
}

export interface SwitchOrganizationRequest {
  organizationId: string;
  refreshToken: string;
}

/**
 * Consume a magic link and get authentication token
 */
export const consumeMagicLink = async (
  spell: string,
  alias: string
): Promise<AuthenticationResponse> => {
  const response = await publicApi.get<AuthenticationResponse>(
    `/api/v1/auth/${spell}/${alias}`
  );

  return response.data;
};

/**
 * Switch to a different organization context
 */
export const switchOrganization = async (
  request: SwitchOrganizationRequest
): Promise<AuthenticationResponse> => {
  const response = await publicApi.post<AuthenticationResponse>(
    '/api/v1/auth/switch-organization',
    {
      organizationId: request.organizationId,
      refreshToken: request.refreshToken,
    }
  );

  return response.data;
};

// Magic Code Login Types
export interface MagicCodeRequestDto {
  email: string;
}

export interface MagicCodeVerifyDto {
  email: string;
  code: string;
}

export interface MagicCodeRequestResponse {
  message: string;
}

export interface RefreshTokenResponse {
  identityToken: string;
  refreshToken: string;
}

/**
 * Refresh the access token using a refresh token
 */
export const refreshToken = async (
  token: string
): Promise<RefreshTokenResponse> => {
  const response = await publicApi.post<RefreshTokenResponse>(
    '/api/v1/auth/refresh',
    { refreshToken: token },
    {
      timeout: 10000, // 10 second timeout
    }
  );

  return response.data;
};

/**
 * Request a magic code for email-based login
 */
export const requestMagicCode = async (
  request: MagicCodeRequestDto
): Promise<MagicCodeRequestResponse> => {
  const response = await publicApi.post<MagicCodeRequestResponse>(
    '/api/v1/auth/magic-code/request',
    request,
    {
      timeout: 15000, // 15 second timeout
    }
  );

  return response.data;
};

/**
 * Verify magic code and authenticate
 */
export const verifyMagicCode = async (
  request: MagicCodeVerifyDto
): Promise<AuthenticationResponse> => {
  const response = await publicApi.post<AuthenticationResponse>(
    '/api/v1/auth/magic-code/verify',
    request,
    {
      timeout: 15000, // 15 second timeout
    }
  );

  return response.data;
};

/**
 * Logout current session by revoking the refresh token.
 * The endpoint is unauthenticated — possession of the refresh token is
 * sufficient to revoke it — so no identity token or interceptors are needed.
 */
export const logoutApi = async (refreshToken: string): Promise<void> => {
  try {
    await publicApi.post('/api/v1/auth/logout', { refreshToken });
  } catch (error) {
    console.warn('Server logout failed:', error);
  }
};

/**
 * Logout all devices by revoking all refresh tokens for the user.
 * The endpoint is unauthenticated — possession of the refresh token is
 * sufficient to identify the user — so no identity token or interceptors are needed.
 */
export const logoutAllApi = async (refreshToken: string): Promise<void> => {
  try {
    await publicApi.post('/api/v1/auth/logout', {
      refreshToken,
      logoutAll: true,
    });
  } catch (error) {
    console.warn('Server logout-all failed:', error);
  }
};
